import Phaser from "phaser";
import { COLORS, FAMILY_BODY, FAMILY_DISPLAY, FAMILY_HEADING, GAME_HEIGHT, GAME_WIDTH } from "../util/constants";
import { ARCS, type CinematicId, type DialogBeat, type StageId, type StoryArc } from "../story/beats";
import { ensureBackdropTexture, BACKDROPS } from "../art/BackdropArt";
import { getMusic, MUSIC, type MusicKey } from "../audio/Music";
import { ensurePortraitTexture, PORTRAIT_W, PORTRAIT_H } from "../art/PortraitArt";
import { drawPanel } from "../ui/Panel";
import { Button } from "../ui/Button";
import { showRingTableau, type RingTableau } from "./story/RingTableau";
import { CINEMATIC_MUSIC, playCinematic, showStage, type Stage } from "./story/Cinematics";
import { speakBlip } from "../ui/voice";
import { paginateBody, maxLinesFor } from "../ui/fitText";
import { sfxClick, sfxPageTurn } from "../audio/Sfx";
import { ENEMY_PALETTES, PLAYER_PALETTES } from "../art/palettes";
import { battleById } from "../data/battles";
import { trackArcStarted } from "../util/analytics";
import { getSevenPath, loadSave, unlockBattle, writeSave } from "../util/save";
import { ROMANCE_FLAG } from "../data/romance";
import type { ArcId, RouteRef } from "../data/contentIds";

interface PortraitMeta {
  primary: number; secondary: number; accent: number; skin: number; hair: number;
  hairStyle?: "short" | "long" | "tied" | "bald" | "hooded";
  beard?: boolean; scar?: boolean; seed: number;
}

const PORTRAIT_TABLE: Record<string, PortraitMeta> = {
  amar:    { ...PLAYER_PALETTES.amar,    hairStyle: "short", seed: 1 },
  lucian:  { ...PLAYER_PALETTES.lucian,  hairStyle: "short", beard: true, seed: 2 },
  ning:    { ...PLAYER_PALETTES.ning,    hairStyle: "tied",  seed: 3 },
  maya:    { ...PLAYER_PALETTES.maya,    hairStyle: "long",  seed: 4 },
  leo:     { ...PLAYER_PALETTES.leo,     hairStyle: "short", seed: 5 },
  ranatoli:{ ...PLAYER_PALETTES.ranatoli,hairStyle: "short", beard: true, seed: 6 },
  selene:  { ...PLAYER_PALETTES.selene,  hairStyle: "long",  scar: true, seed: 7 },
  kian:    { ...ENEMY_PALETTES.kian,     hairStyle: "short", seed: 11 },
  ndari:   { ...ENEMY_PALETTES.ndari,    hairStyle: "long",  seed: 12 },
  nebu:    { ...ENEMY_PALETTES.archbold, hairStyle: "short", beard: true, seed: 13 },
  // Coyne — procedural fallback until public/assets/portraits/coyne.png
  // ships; the real PNG (loaded as portrait:coyne) takes precedence.
  coyne:   { ...ENEMY_PALETTES.archbold, hairStyle: "short", beard: true, seed: 14 }
};

// Typed against StoryArc["music"] so adding a new music slug to the union in
// beats.ts without adding a row here is a compile error — was Record<string,
// MusicKey>, which silently returned undefined and crashed at runtime.
const arcMusic: Record<StoryArc["music"], MusicKey> = {
  emotionalLife: MUSIC.emotionalLife,
  adventureAnthros: MUSIC.adventureAnthros,
  lifeInGrude: MUSIC.lifeInGrude,
  danger: MUSIC.danger,
  battlePrep: MUSIC.battlePrep,
  mainTheme: MUSIC.mainTheme,
  emotional: MUSIC.emotional,
  everydayLife: MUSIC.everydayLife,
  trailer: MUSIC.trailer,
  ravageDaredevil: MUSIC.ravageDaredevil,
  sadness: MUSIC.sadness,
  sadness2: MUSIC.sadness2,
  grudeBattle1: MUSIC.grudeBattle1,
  death: MUSIC.death
};

interface StoryArgs { arcId: ArcId; }

// Portrait display area — anchored above the dialog panel.
// Uses cover-fit (Math.max) + top-anchor so faces stay legible regardless of
// source aspect (square / 2:3 portrait / 3:2 landscape). A bottom gradient
// fade hides the crop seam where the portrait meets the dialog panel.
const PORTRAIT_AREA_W = 300;
const PORTRAIT_AREA_H = 360;
const PORTRAIT_MASK_KEY = "story_portrait_fade_mask";

// Dialog panel layout. Hoisted to module scope so create() and showBeat()
// don't drift apart when one is tweaked. The box grows UPWARD from a fixed
// bottom edge (PANEL_BOTTOM) so the Continue / Skip buttons pinned near the
// screen bottom stay put. The body renders at a FIXED, comfortable font; a
// beat too long for the box splits into click-through pages ("More ▾")
// rather than shrinking the text. Most beats fit one page after the
// script-wide verbosity pass; the few long ones page cleanly.
const PANEL_X = 120;
const PANEL_W = GAME_WIDTH - 240;
const PANEL_H = 230;
const PANEL_BOTTOM = GAME_HEIGHT - 60;
const PANEL_Y = PANEL_BOTTOM - PANEL_H;
const SPEAKER_Y_OFFSET = 14;
const BODY_Y_OFFSET = 44;
const BODY_BOTTOM_PADDING = 16;
// Fixed body font + line spacing (must match the bodyText style in create()).
// maxLinesFor turns the available box height into a per-page line budget.
const BODY_FONT_SIZE = 21;
const BODY_LINE_SPACING = 10;
const BODY_MAX_HEIGHT = PANEL_H - BODY_Y_OFFSET - BODY_BOTTOM_PADDING;
const BODY_MAX_LINES = maxLinesFor(BODY_FONT_SIZE, BODY_LINE_SPACING, BODY_MAX_HEIGHT);

export class StoryScene extends Phaser.Scene {
  private arcId!: ArcId;
  private idx = 0;
  private bodyText!: Phaser.GameObjects.Text;
  private speakerText!: Phaser.GameObjects.Text;
  private portrait?: Phaser.GameObjects.Image;
  private tableau?: RingTableau;
  // A staged picture behind the dialogue (DialogBeat.stage), and which.
  private stage?: Stage;
  private stageId?: StageId;
  // A cinematic is playing over the scene: the dialogue waits for it.
  private cineRunning = false;
  private endCinePlayed = false;
  // The dialogue's own furniture, hidden while a cinematic plays.
  private uiParts: Phaser.GameObjects.GameObject[] = [];
  private beats: DialogBeat[] = [];
  private titleText?: Phaser.GameObjects.Text;
  private subtitleText?: Phaser.GameObjects.Text;
  private continueBtn!: Button;
  private skipBtn!: Button;
  private bgImage!: Phaser.GameObjects.Image;
  private revealing = false;
  // Speaking-portrait life: the current beat's voice id for blips, the
  // portrait's authored base scale, and the pulse tween that breathes
  // while text types.
  private speakingId?: string;
  private portraitBaseScale = 1;
  private speakPulse?: Phaser.Tweens.Tween;
  private fullText = "";
  // Pagination state. A "beat" can span multiple "pages" if its body wraps to
  // more lines than fit in the panel. The portrait + speaker name stay fixed
  // across pages of the same beat; only the body text steps.
  private currentBeatPages: string[] = [];
  private currentPageIdx = 0;

  constructor() { super("StoryScene"); }

  init(data: StoryArgs): void {
    this.arcId = data.arcId;
    this.idx = 0;
    this.tableau = undefined;
    this.stage = undefined;
    this.stageId = undefined;
    this.cineRunning = false;
    this.endCinePlayed = false;
    this.uiParts = [];
    this.titleText = undefined;
    this.subtitleText = undefined;
    // The arc as this save tells it: beats for another marriage drop out
    // (DialogBeat.partner).
    const married = String(loadSave().flags[ROMANCE_FLAG] ?? "none");
    this.beats = (ARCS[this.arcId]?.beats ?? []).filter((b) => !b.partner || b.partner === married);
    // The scene is reused for every arc; these belonged to the last one.
    this.portrait = undefined;
    this.speakPulse = undefined;
  }

  create(): void {
    const arc = ARCS[this.arcId];
    if (!arc) {
      this.scene.start("OverworldScene");
      return;
    }

    // Analytics — track arc entries so we can see how far into the script
    // the average player reaches.
    trackArcStarted(this.arcId);

    // Backdrop honors arc.backdrop when set; falls back to thuling. Real PNG
    // (loaded as `backdrop:<id>`) is preferred over the procedural fallback.
    const bdName = arc.backdrop ?? "thuling";
    const bdSpec = BACKDROPS[bdName];
    const bgKey = ensureBackdropTexture(this, `bg_${bdName}_story`, bdSpec, `backdrop:${bdName}`);
    // The backdrop and its vignette sit under any staged picture (see
    // DialogBeat.tableau), which sits under the panel and the text.
    this.bgImage = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey).setDisplaySize(GAME_WIDTH, GAME_HEIGHT).setDepth(-20);
    this.bgImage.setAlpha(0.85);
    // Slow Ken-Burns drift
    this.tweens.add({ targets: this.bgImage, x: GAME_WIDTH / 2 + 20, y: GAME_HEIGHT / 2 - 6, duration: 8000, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });

    // Vignette
    const v = this.add.graphics().setDepth(-19);
    v.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.45, 0.45, 0.85, 0.85);
    v.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Title banner
    this.titleText = this.add.text(GAME_WIDTH / 2, 60, arc.title, {
      fontFamily: FAMILY_DISPLAY,
      fontSize: "34px",
      color: "#f4d999",
      stroke: "#1a0e04",
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 3, color: "#000", blur: 12, fill: true, stroke: true }
    }).setOrigin(0.5).setLetterSpacing(2);

    if (arc.subtitle) {
      this.subtitleText = this.add.text(GAME_WIDTH / 2, 100, arc.subtitle, {
        fontFamily: FAMILY_BODY,
        fontSize: "17px",
        color: "#c9b07a",
        fontStyle: "italic",
        shadow: { offsetX: 0, offsetY: 2, color: "#000", blur: 6, fill: true }
      }).setOrigin(0.5).setLetterSpacing(1);
    }

    // Dialog panel
    const pg = this.add.graphics();
    drawPanel(pg, PANEL_X, PANEL_Y, PANEL_W, PANEL_H);

    // Text is left-aligned inside the panel; the speaker portrait floats above
    // the panel on the RIGHT side. (Earlier layout reserved the LEFT for the
    // portrait, which pushed the dialogue ~340px in and made the text feel
    // shoved against the right edge.)
    const textLeft = PANEL_X + 24;

    this.speakerText = this.add.text(textLeft, PANEL_Y + SPEAKER_Y_OFFSET, "", {
      fontFamily: FAMILY_HEADING,
      fontSize: "22px",
      color: "#f4d999",
      stroke: "#000",
      strokeThickness: 3,
      shadow: { offsetX: 0, offsetY: 2, color: "#000", blur: 6, fill: true }
    }).setLetterSpacing(1);

    this.bodyText = this.add.text(textLeft, PANEL_Y + BODY_Y_OFFSET, "", {
      fontFamily: FAMILY_BODY,
      fontSize: "21px",
      color: "#f3ecd9",
      stroke: "#000",
      strokeThickness: 2,
      shadow: { offsetX: 0, offsetY: 2, color: "#000", blur: 6, fill: true },
      wordWrap: { width: PANEL_W - PORTRAIT_AREA_W - 80 },
      lineSpacing: 10
    }).setLetterSpacing(0.3);

    this.continueBtn = new Button(this, {
      x: GAME_WIDTH - 200,
      y: GAME_HEIGHT - 56,
      w: 160, h: 40,
      label: "Continue ▸",
      primary: true,
      fontSize: 16,
      onClick: () => this.advance()
    });

    this.skipBtn = new Button(this, {
      x: 40,
      y: GAME_HEIGHT - 56,
      w: 120, h: 40,
      label: "Skip ▸▸",
      primary: false,
      fontSize: 14,
      onClick: () => {
        sfxClick();
        this.finishArc();
      }
    });

    this.input.keyboard?.on("keydown-SPACE", () => this.advance());
    this.input.keyboard?.on("keydown-ENTER", () => this.advance());
    this.input.on("pointerdown", () => this.advance());

    this.uiParts = [pg, this.speakerText, this.bodyText, this.continueBtn, this.skipBtn,
      ...[this.titleText, this.subtitleText].filter((t): t is Phaser.GameObjects.Text => !!t)];
    this.cameras.main.fadeIn(450, 0, 0, 0);
    // An arc can open on a cinematic (StoryArc.cinematic): the film first,
    // then the dialogue fades up under it.
    if (arc.cinematic) {
      getMusic(this).play(arcMusic[CINEMATIC_MUSIC[arc.cinematic]], { fadeMs: 800 });
      void this.runCinematic(arc.cinematic, true).then(() => {
        getMusic(this).play(arcMusic[arc.music], { fadeMs: 1400 });
        this.showBeat(this.beats[0]!);
      });
      return;
    }
    getMusic(this).play(arcMusic[arc.music], { fadeMs: 800 });
    this.showBeat(this.beats[0]!);
  }

  /** Play a cinematic over the scene with the dialogue hidden under it. */
  private async runCinematic(id: CinematicId, restoreUi: boolean): Promise<void> {
    this.cineRunning = true;
    const parts = [...this.uiParts, ...(this.portrait ? [this.portrait] : [])];
    for (const o of parts) (o as unknown as Phaser.GameObjects.Components.Alpha).setAlpha(0);
    await playCinematic(this, id, 1000);
    this.cineRunning = false;
    if (restoreUi && this.sys.isActive()) {
      // The title stays down when a stage or tableau has already claimed the top.
      const heads = new Set<Phaser.GameObjects.GameObject>([this.titleText, this.subtitleText].filter((t): t is Phaser.GameObjects.Text => !!t));
      const back = parts.filter((o) => !(heads.has(o) && (this.stage || this.tableau)));
      this.tweens.add({ targets: back, alpha: 1, duration: 600, ease: "Sine.easeOut" });
    }
  }

  private showBeat(beat: DialogBeat): void {
    // Mid-arc music turn — see DialogBeat.music. Same-track requests
    // no-op inside MusicManager, so this only sounds when a beat
    // genuinely changes the score.
    if (beat.music) {
      getMusic(this).play(arcMusic[beat.music], { fadeMs: 1400 });
    }
    // Portrait + speaker change only between beats, not between pages of the
    // same beat. Keep this work outside showCurrentPage().
    this.speakingId = beat.portraitId;
    // A staged picture, from this beat to the end of the arc. The arc's
    // title steps aside for it.
    if (beat.stage && beat.stage !== this.stageId) {
      // A new stage replaces the old one, crossfading.
      this.stage?.destroy(1300);
      this.stage = showStage(this, beat.stage, -18);
      this.stageId = beat.stage;
      const heads = [this.titleText, this.subtitleText].filter((t): t is Phaser.GameObjects.Text => !!t);
      this.tweens.add({ targets: heads, alpha: 0, duration: 900 });
    }
    if (beat.tableau && !this.tableau) {
      this.tableau = showRingTableau(this, beat.tableau.partner, -10, beat.tableau.kind);
      const heads = [this.titleText, this.subtitleText].filter((t): t is Phaser.GameObjects.Text => !!t);
      this.tweens.add({ targets: heads, alpha: 0, duration: 900 });
    }
    if (this.portrait) { this.portrait.destroy(); this.portrait = undefined; }
    if (beat.portraitId && beat.portraitId !== "narrator") {
      const key = this.resolvePortraitKey(beat.portraitId, beat.expression);
      if (key) {
        // Portrait sits on the RIGHT side of the panel now (text on the left).
        // Top-center of the portrait region; the image extends downward and
        // overlaps the dialog panel by ~24px, hidden by the gradient mask.
        const areaCenterX = PANEL_X + PANEL_W - 24 - PORTRAIT_AREA_W / 2;
        const areaTopY = PANEL_Y - PORTRAIT_AREA_H + 24;
        this.portrait = this.add.image(areaCenterX, areaTopY, key).setOrigin(0.5, 0);
        const tex = this.textures.get(key).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
        const srcW = tex.width || PORTRAIT_W;
        const srcH = tex.height || PORTRAIT_H;
        // Cover fit: fill the entire area, cropping overflow. Faces stay
        // large and consistent across square / portrait / landscape sources.
        const scale = Math.max(PORTRAIT_AREA_W / srcW, PORTRAIT_AREA_H / srcH);
        this.portrait.setScale(scale);
        this.portraitBaseScale = scale;
        // Mask the bottom edge with a vertical gradient so the cropped
        // boundary feathers into the dialog panel instead of cutting hard.
        this.portrait.setMask(this.ensurePortraitMask(areaCenterX, areaTopY).createBitmapMask());
      }
    }

    this.speakerText.setText(beat.speaker ?? (beat.portraitId === "narrator" ? "" : ""));

    // Paginate the beat at the fixed display font: most beats are a single
    // page; a long one splits into click-through pages the player advances
    // with the "More ▾" button. No font shrinking.
    this.currentBeatPages = paginateBody(this.bodyText, beat.body, BODY_MAX_LINES);
    this.currentPageIdx = 0;
    this.showCurrentPage();
  }

  // Renders the current page's text with the typewriter reveal. Called by
  // showBeat (first page of a new beat) and by advance() (next page of the
  // same beat). Updates the Continue button's label so the player can tell
  // mid-beat pagination apart from beat-to-beat advance.
  private showCurrentPage(): void {
    sfxPageTurn();
    const page = this.currentBeatPages[this.currentPageIdx] ?? "";
    this.fullText = page;
    this.bodyText.setText("");
    this.revealing = true;
    this.setSpeaking(true);
    const hasMore = this.currentPageIdx + 1 < this.currentBeatPages.length;
    this.continueBtn.setLabel(hasMore ? "More ▾" : "Continue ▸");
    let i = 0;
    const reveal = () => {
      if (!this.revealing) {
        this.bodyText.setText(this.fullText);
        this.setSpeaking(false);
        return;
      }
      i++;
      this.bodyText.setText(this.fullText.slice(0, i));
      // Voice blip on every other letterlike character — the cadence
      // that reads as speech without machine-gunning the ear.
      const ch = this.fullText[i - 1] ?? "";
      if (i % 2 === 0 && /[a-zA-Z0-9]/.test(ch)) speakBlip(this.speakingId);
      if (i < this.fullText.length) {
        this.time.delayedCall(20, reveal);
      } else {
        this.revealing = false;
        this.setSpeaking(false);
      }
    };
    reveal();
  }

  // Returns a positioned Image whose alpha runs from 1.0 at the top to 0 at
  // the bottom 30% — used as a BitmapMask for the portrait so its bottom
  // crop fades into the dialog panel.
  // Speaking-state hook. The voice blips carry the "talking" feel; the
  // portrait itself stays STILL — an earlier version scale-pulsed it,
  // but portraits are top-anchored so the growth read as the portrait
  // bobbing up and down. Kept as a hook (and a defensive scale reset)
  // in case a better-behaved life effect lands later.
  private setSpeaking(_on: boolean): void {
    if (this.speakPulse) { this.speakPulse.stop(); this.speakPulse = undefined; }
    if (this.portrait) this.portrait.setScale(this.portraitBaseScale);
  }

  private ensurePortraitMask(centerX: number, topY: number): Phaser.GameObjects.Image {
    if (!this.textures.exists(PORTRAIT_MASK_KEY)) {
      const tex = this.textures.createCanvas(PORTRAIT_MASK_KEY, PORTRAIT_AREA_W, PORTRAIT_AREA_H);
      if (tex) {
        const ctx = tex.getContext();
        const grad = ctx.createLinearGradient(0, 0, 0, PORTRAIT_AREA_H);
        grad.addColorStop(0, "rgba(255,255,255,1)");
        grad.addColorStop(0.70, "rgba(255,255,255,1)");
        grad.addColorStop(0.92, "rgba(255,255,255,0.35)");
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, PORTRAIT_AREA_W, PORTRAIT_AREA_H);
        tex.refresh();
      }
    }
    const mask = this.add.image(centerX, topY, PORTRAIT_MASK_KEY).setOrigin(0.5, 0);
    mask.setVisible(false);
    return mask;
  }

  private resolvePortraitKey(id: string, expression?: string): string | null {
    // 1) Expression variant from disk (e.g., portrait:amar:resolute).
    if (expression) {
      const exprKey = `portrait:${id}:${expression}`;
      if (this.textures.exists(exprKey)) return exprKey;
    }
    // 2) Default real portrait from disk (e.g., portrait:amar).
    const realKey = `portrait:${id}`;
    if (this.textures.exists(realKey)) return realKey;
    // 3) Procedural fallback for characters with palette metadata.
    const meta = PORTRAIT_TABLE[id];
    if (meta) {
      return ensurePortraitTexture(this, { id, ...meta, artSeed: meta.seed }, expression);
    }
    return null;
  }

  private advance(): void {
    // A cinematic owns the input while it plays (it has its own skip).
    if (this.cineRunning) return;
    // Mid-typewriter: complete the current page instantly instead of
    // advancing. Prevents accidentally skipping a page if the player clicks
    // before the reveal finishes.
    if (this.revealing) {
      this.revealing = false;
      this.setSpeaking(false);
      this.bodyText.setText(this.fullText);
      // Re-evaluate the button label now that the page is fully shown.
      const hasMore = this.currentPageIdx + 1 < this.currentBeatPages.length;
      this.continueBtn.setLabel(hasMore ? "More ▾" : "Continue ▸");
      return;
    }
    // More pages of the current beat? Step within the beat instead of moving
    // to the next one. Portrait + speaker stay; only the body text changes.
    if (this.currentPageIdx + 1 < this.currentBeatPages.length) {
      this.currentPageIdx++;
      this.showCurrentPage();
      return;
    }
    // Story-gated promotion: if this beat carries a `promote` field, fire
    // PromotionScene as a paused overlay before moving on. The promotion
    // mutates the save synchronously inside PromotionScene.create(); when
    // the player closes it, this scene resumes and we step to the next
    // beat. Idempotent — re-entering an already-fired promote beat (via
    // DevJump or save-state restore) is a no-op for the save mutation.
    const arc = ARCS[this.arcId];
    if (!arc) return;
    const currentBeat = this.beats[this.idx];
    if (currentBeat?.promote) {
      this.scene.pause();
      this.scene.run("PromotionScene", {
        characterId: currentBeat.promote,
        resumeKey: this.scene.key
      });
      // When PromotionScene closes, this scene resumes — but we still
      // need to advance past the promote beat. Listen for the resume
      // event once and step to the next beat.
      this.events.once(Phaser.Scenes.Events.RESUME, () => {
        this.idx++;
        if (this.idx >= this.beats.length) {
          this.finishArc();
          return;
        }
        this.showBeat(this.beats[this.idx]!);
      });
      return;
    }
    // Otherwise advance to the next beat (or end the arc).
    this.idx++;
    if (this.idx >= this.beats.length) {
      this.finishArc();
      return;
    }
    this.showBeat(this.beats[this.idx]!);
  }

  private finishArc(): void {
    const arc = ARCS[this.arcId];
    if (!arc) { this.scene.start("OverworldScene"); return; }
    if (this.cineRunning) return;
    // An arc can close on a cinematic (StoryArc.endCinematic) before it
    // hands on.
    if (arc.endCinematic && !this.endCinePlayed) {
      this.endCinePlayed = true;
      this.revealing = false;
      getMusic(this).play(arcMusic[CINEMATIC_MUSIC[arc.endCinematic]], { fadeMs: 1200 });
      void this.runCinematic(arc.endCinematic, false).then(() => this.finishArc());
      return;
    }
    this.cameras.main.fadeOut(450, 0, 0, 0);
    this.cameras.main.once("camerafadeoutcomplete", () => this.routeNext(arc.next));
  }

  // `next` is a discriminated union of "credits" | "overworld" |
  // `story:${ArcId}` | `prep:${BattleId}`. Because the prefixed variants are
  // template literal types, the slice'd suffix is provably an ArcId / BattleId
  // — no runtime check needed once the prefix matches. Anything outside the
  // union fails to type-check upstream in beats.ts.
  private routeNext(next: RouteRef): void {
    if (next === "credits") {
      this.scene.start("CreditsScene");
      return;
    }
    if (next === "camp") {
      this.scene.start("CampScene");
      return;
    }
    if (next === "choice") {
      // Seven Paths divergence (post-B18). ChoiceScene presents the seven
      // philosophies, persists the pick, and routes onward to the chosen
      // path's opener.
      this.scene.start("ChoiceScene");
      return;
    }
    if (next === "romance") {
      // The marriage question (war-path endings). RomanceScene offers
      // the path's two partners or walking on alone, persists the pick,
      // and routes into the matching wed_*/end_alone coda arc.
      this.scene.start("RomanceScene");
      return;
    }
    if (next === "another_path") {
      // Post-credits: Khione's offer to walk a road not taken.
      this.scene.start("AnotherPathScene");
      return;
    }
    if (next === "ending") {
      // Per-path campaign ending — post_path_final ends here and the
      // saved war path picks the coda. Defensive credits fall-through if
      // no path is set (unreachable in a normal run).
      const path = getSevenPath(loadSave());
      const coda =
        path === "vengeance" || path === "restoration" || path === "revolution" ||
        path === "duty" || path === "mercy"
          ? (`post_ending_${path}` as ArcId)
          : null;
      if (coda) this.scene.start("StoryScene", { arcId: coda });
      else this.scene.start("CreditsScene");
      return;
    }
    if (next === "overworld") {
      // Legacy route, kept as an escape hatch for arcs that explicitly
      // want to drop the player into the world map without going
      // through camp. New arcs should prefer "camp".
      this.scene.start("OverworldScene");
      return;
    }
    if (next.startsWith("story:")) {
      const id = next.slice("story:".length) as ArcId;
      this.scene.start("StoryScene", { arcId: id });
      return;
    }
    if (next.startsWith("prep:")) {
      const id = next.slice("prep:".length);
      const node = battleById(id);
      if (node) {
        // Between chapters, the story hands the player back to camp with
        // this battle set as the next chapter ("Start Next Chapter" opens
        // its prep). The opening scenes lead straight into the first
        // battle as before: there is no camp to return to yet.
        const save = loadSave();
        if (save.completedBattles.length === 0) {
          this.scene.start("BattlePrepScene", { battleId: node.id });
          return;
        }
        // Unlocked too: the story is sending the squad there (its victory
        // has normally unlocked it already), and the camp only offers a
        // chapter that is open.
        writeSave({ ...unlockBattle(save, node.id), nextChapter: node.id });
        this.scene.start("CampScene", { nextChapter: node.id });
        return;
      }
    }
    this.scene.start("OverworldScene");
  }
}

void COLORS;
